import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useTableFilters } from '@/components/data-table/filters-v2';
import { usePersistedTableFilters } from '@/hooks/use-persisted-table-filters';
import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { ApiError } from '@/modules/common/api/errors';
import { MemberStatus } from '@/modules/team';

import { InviteFailure } from '../../components/member-access/invite-failures-dialog';
import {
  MEMBER_ACCESS_FILTER_KEY_STATUSES,
  MEMBER_ACCESS_TABLE_FILTER_KEYS,
  MEMBER_ACCESS_TABLE_FILTERS_STORAGE_KEY,
} from '../../constants/member-access-filter-keys';
import { getMemberAccessFiltersConfig } from '../../constants/member-access-filters-config';
import { buildInvitePayload, buildUpdatePayload } from '../../store/member-edits.payload';
import {
  countGroupChanges,
  countSelected,
  useMemberEditsStore,
} from '../../store/member-edits.store';
import { MemberAccessRow } from '../../types/member-access';
import { isInvitable } from '../../utils/member-email';
import { useBulkInviteTeamMembers } from './use-bulk-invite-team-members';
import { useMemberAccess } from './use-member-access';
import { useMemberAccessTable } from './use-member-access-table';
import { useUpdateMemberAccess } from './use-update-member-access';
import { useUserGroups, useUserGroupsList } from './use-user-groups';

const STATUS_KEY = MEMBER_ACCESS_FILTER_KEY_STATUSES;

const DEFAULT_STATUS_FILTER = [
  MemberStatus.Active,
  MemberStatus.Invited,
  MemberStatus.Expired,
  MemberStatus.Basic,
];

/**
 * Controller hook for the Members access-groups page. Owns all business logic —
 * queries, the edits store wiring, invite-mode lifecycle, filter pinning, and the
 * save/invite flows — so the page component is purely presentational.
 *
 * Edits and selection live in `useMemberEditsStore` (keyed by userId), decoupled
 * from the visible/loaded window, which is what makes filtering non-destructive and
 * keeps change-tracking alive across pagination (OPS-1500).
 */
export const useMemberAccessPage = () => {
  const { t } = useTranslation(['authenticated.settings', 'authenticated.team']);

  const { isReady: areFiltersReady } = usePersistedTableFilters({
    storageKey: MEMBER_ACCESS_TABLE_FILTERS_STORAGE_KEY,
    filterKeys: MEMBER_ACCESS_TABLE_FILTER_KEYS,
    defaultFilters: { [STATUS_KEY]: DEFAULT_STATUS_FILTER },
  });

  const { groupOptions, isLoading: isLoadingGroups } = useUserGroups();

  // Raw groups (same query key, so no extra fetch) — needed for `isDefault`, which the
  // `groupOptions` select collapses away.
  const { data: userGroups } = useUserGroupsList();
  const defaultGroup = useMemo(
    () => userGroups?.find(group => group.isDefault) ?? null,
    [userGroups]
  );
  const defaultGroupId = defaultGroup ? String(defaultGroup.id) : null;

  const [isInviteMode, setIsInviteMode] = useState(false);
  const [inviteFailures, setInviteFailures] = useState<InviteFailure[]>([]);

  // In invite mode the status filter is pinned to Basic and locked from editing.
  const filtersConfig = useMemo(
    () => getMemberAccessFiltersConfig({ groupOptions, isStatusLocked: isInviteMode }),
    [groupOptions, isInviteMode]
  );

  const filterState = useTableFilters(filtersConfig);
  const { activeFilters, setFilter, clearFilter, getFilterValue } = filterState;

  const { data, isLoading, error, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useMemberAccess({
      // In invite mode the status filter is already pinned to Basic in the URL (see
      // enter/exit handlers); other filters apply as selected.
      filters: activeFilters,
      enabled: areFiltersReady,
    });

  const visibleRows = useMemo(() => data?.items ?? [], [data]);

  // ----- Edits store wiring (single live source for edits + selection + counts) -----
  const hydrate = useMemberEditsStore(state => state.hydrate);
  const rebaseline = useMemberEditsStore(state => state.rebaseline);
  const clearAll = useMemberEditsStore(state => state.clearAll);
  const resetAllEdits = useMemberEditsStore(state => state.resetAllEdits);
  const deselectUsers = useMemberEditsStore(state => state.deselectUsers);
  const setSelectedMany = useMemberEditsStore(state => state.setSelectedMany);

  // Primitive selectors — the page re-renders only when a COUNT changes, never per keystroke.
  const changesCount = useMemberEditsStore(state => countGroupChanges(state.edits, state.baseline));
  const selectedCount = useMemberEditsStore(state => countSelected(state.edits));

  // Seed edits/baseline for newly-loaded rows. hydrate is additive + no-op-stable, so
  // scrolling cannot reset in-progress edits (OPS-1500) and the new query object each
  // fetch does not churn renders.
  useEffect(() => {
    if (visibleRows.length) hydrate(visibleRows);
  }, [visibleRows, hydrate]);

  // Clear the store on mount AND unmount: it's a module singleton, so this prevents
  // cross-mount bleed and lets a fresh fetch re-hydrate after navigating back.
  useEffect(() => {
    clearAll();
    return () => clearAll();
  }, [clearAll]);

  // Trap-B-safe auto-deselect: depends ONLY on a stable primitive signature and calls
  // an idempotent setter, so it cannot loop. Drops selection for members that became
  // invalid (group cleared / email invalid) while selected.
  const invalidSelectedSig = useMemberEditsStore(state =>
    Object.entries(state.edits)
      .filter(
        ([, edit]) =>
          edit.selected &&
          !isInvitable({
            groupId: edit.groupId,
            email: edit.email,
            hasDefaultGroup: defaultGroupId !== null,
          })
      )
      .map(([id]) => id)
      .sort()
      .join(',')
  );

  useEffect(() => {
    if (!isInviteMode || invalidSelectedSig === '') return;
    deselectUsers(invalidSelectedSig.split(',').map(Number));
  }, [isInviteMode, invalidSelectedSig, deselectUsers]);

  const { table, saveColumnSizing } = useMemberAccessTable({
    data: visibleRows,
    groupOptions,
    isInviteMode,
    defaultGroup,
  });

  const updateMemberAccessMutation = useUpdateMemberAccess();
  const bulkInviteTeamMembersMutation = useBulkInviteTeamMembers();

  // ----- Invite-mode lifecycle -----
  // Status filter selected before entering invite mode, restored on exit.
  const preInviteStatusRef = useRef<string[] | null>(null);

  const enterInviteMode = useCallback(() => {
    // Stash the current status selection, then pin the visible status filter to Basic
    // so the filters above the table reflect what the table actually shows.
    preInviteStatusRef.current = getFilterValue(STATUS_KEY);
    setFilter(STATUS_KEY, [MemberStatus.Basic]);
    setIsInviteMode(true);
  }, [getFilterValue, setFilter]);

  const exitInviteMode = useCallback(() => {
    setIsInviteMode(false);
    const allIds = Object.keys(useMemberEditsStore.getState().edits).map(Number);
    setSelectedMany(allIds, false);

    // Restore the status filter the user had before entering invite mode.
    const previous = preInviteStatusRef.current;
    if (previous && previous.length > 0) {
      setFilter(STATUS_KEY, previous);
    } else {
      clearFilter(STATUS_KEY);
    }
    preInviteStatusRef.current = null;
  }, [setSelectedMany, setFilter, clearFilter]);

  // ----- Save / Invite flows (read the union of edits across all filters) -----
  const handleSave = useCallback(async () => {
    const { edits, baseline } = useMemberEditsStore.getState();
    const items = buildUpdatePayload(edits, baseline);
    if (items.length === 0) return;

    try {
      await updateMemberAccessMutation.mutateAsync(items);
      // Re-baseline only the saved members so changesCount returns to 0 without an
      // empty-store flash and without dropping unrelated in-progress edits. The saved
      // group value lives in `edits`; other fields come from the existing baseline.
      const savedRows: MemberAccessRow[] = items.map(item => {
        const base = baseline[item.teamMemberId];
        return {
          userId: item.teamMemberId,
          name: base?.name ?? '',
          email: base?.email ?? null,
          profileImageUrl: null,
          groupId: String(item.userGroupId),
          groupName: null,
          status: base?.status ?? MemberStatus.Active,
        };
      });
      rebaseline(savedRows);
    } catch {
      // Error handled by the mutation.
    }
  }, [updateMemberAccessMutation, rebaseline]);

  const handleInviteAndSave = useCallback(async () => {
    const { edits, baseline } = useMemberEditsStore.getState();

    const invitePayload = buildInvitePayload({ edits, defaultGroupId });
    if (invitePayload.length === 0) return;

    try {
      // (a) Persist ALL group edits first (across all filters; email travels in the invite payload).
      const updates = buildUpdatePayload(edits, baseline);
      if (updates.length > 0) {
        await updateMemberAccessMutation.mutateAsync(updates);
      }

      // (b) Bulk-invite the SELECTED members, including any filtered out of view.
      const result = await bulkInviteTeamMembersMutation.mutateAsync({ members: invitePayload });

      const failures: InviteFailure[] = result.results
        .filter(r => !r.success)
        .map(r => ({
          name: baseline[r.teamMemberId]?.name ?? String(r.teamMemberId),
          email: edits[r.teamMemberId]?.email ?? null,
          errorCode: r.errorCode,
        }));

      if (failures.length > 0) {
        setInviteFailures(failures);
      }

      exitInviteMode();
      // Invited members flip Basic→Invited server-side; clear so the refetch re-hydrates cleanly.
      clearAll();
    } catch {
      // Errors handled by the mutations.
    }
  }, [updateMemberAccessMutation, bulkInviteTeamMembersMutation, exitInviteMode, clearAll]);

  // ----- Navigation guard (real route changes only; filters keep the same pathname) -----
  const unsavedChangesGuard = useUnsavedChangesGuard({
    isDirty: changesCount > 0 || selectedCount > 0 || isInviteMode,
    onDiscard: () => {
      resetAllEdits();
      exitInviteMode();
    },
  });

  const totalMembers = data?.pagination?.total ?? visibleRows.length;
  const errorMessage = error instanceof ApiError ? t(error.code) : error?.message;

  return {
    t,
    // Filters
    filtersConfig,
    filterState,
    // Data + table
    table,
    saveColumnSizing,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
    errorMessage,
    totalMembers,
    isLoadingGroups,
    // Invite mode
    isInviteMode,
    enterInviteMode,
    exitInviteMode,
    inviteFailures,
    clearInviteFailures: useCallback(() => setInviteFailures([]), []),
    // Footer counts + actions
    changesCount,
    selectedCount,
    isSavePending: updateMemberAccessMutation.isPending,
    isInvitePending: bulkInviteTeamMembersMutation.isPending,
    handleSave,
    handleInviteAndSave,
    resetAllEdits,
    // Guard
    unsavedChangesGuard,
  };
};
